import React, {useEffect, useState, useCallback} from 'react';
import utils from '../../utils';
import useAction from '../../store/actions';
import ActionButtons from '../ActionButtons/ActionButtons';
import SearchBox from '../Common/SearchBox/SearchBox';
import AppError from '../../pages/Error/AppError';
import ListViewRow from './ListViewRow';
import ListViewHeader from './ListViewHeader';
import ButtonBasic from '../Common/Buttons/ButtonBasic';
import FiltersSelect from './FiltersSelect';
import ListViewPagination from './ListViewPagination';
import Loader from '../Common/Loader/Loader';
import toast from 'react-hot-toast';
import ListViewTable from './ListViewTable';

/**
 * @export
 * @param {String} globalModule
 * @param {String} prefix
 * @param {Function} popupSelectCallback
 * @param {Array} disabledRecords
 * @param {Array} hiddenRecords
 * @param {Object} userData
 * @param {Boolean} disableMultiple
 * @param {Object} config
 * @param {Object} additionalData
 * @param {String} fromModule
 * @param {Object} defaultFilter
 * @param {Object} predefinedFilter
 * @param {Boolean} onDashboard
 *
 * @return {*}
 */
export default function ListView({
	globalModule,
	prefix,
	popupSelectCallback,
	disabledRecords,
	hiddenRecords,
	userData,
	disableMultiple,
	config,
	additionalData,
	fromModule,
	defaultFilter,
	predefinedFilter,
	onDashboard,
}) {
	const [rows, setRows] = useState([]);
	const [checked, setChecked] = useState(false);
	const [offset, setOffset] = useState(0);
	const [search, setSearch] = useState('');
	const [error, setError] = useState(false);
	const [dataSort, setDataSort] = useState({});
	const [module, setModule] = useState('');
	const [filters, setFilters] = useState(predefinedFilter);
	const [savedFilters, setSavedFilters] = useState([]);
	const [selectedFilter, setSelectedFilter] = useState(0);
	const [popupLoad, setPopupLoad] = useState(false);

	const sAction = useAction();
	const viewData = sAction.dsGet(`${prefix}`);
	const isPopup = prefix !== 'view' ? true : false;
	const listSelected = sAction.dsGet(`${prefix}/list/selected`);

	// On dashboard 30 on list by user settings if none exists 50
	const numberOfRecords = onDashboard ? config?.systemSettings?.dashboardListRows : config?.systemSettings?.numberOfRecords || 50;

	useEffect(() => {
		setChecked(false);
		setOffset(0);
		setDataSort({});
		if (!predefinedFilter) {
			setFilters(null);
		}
		setSavedFilters([]);
		setSearch('');
		setModule(globalModule);
		if (isPopup) {
			setModule(viewData?.module);
		}
	}, [globalModule]);

	useEffect(() => {
		if (module) {
			getList({});
		}
	}, [module]);

	useEffect(() => {
		if ((offset > 0 || search || !search) && module) {
			getList(dataSort, true);
		}
	}, [offset, search]);

	useEffect(() => {
		if (filters !== null) {
			setOffset(0);
			getList(dataSort);
		}
	}, [filters]);

	useEffect(() => {
		setRows(viewData?.list?.data || []);
		if (viewData?.list?.data?.length === 0 || !viewData?.list?.data?.[0]?.checked) {
			setChecked(false);
		}
		let checkedItemsOnCurrentList = [];
		viewData?.list?.data?.forEach((element) => {
			if (listSelected.includes(element.id)) {
				checkedItemsOnCurrentList.push(element.id);
			}
		});

		setChecked(checkedItemsOnCurrentList.length === numberOfRecords);
	}, [viewData?.list?.data]);

	const labelSave = utils.translate('LBL_NOTHING_TO_SAVE');

	/**
	 * @return {Function | void}
	 */
	const saveFilters = () => {
		if (filters == null || Object.keys(filters).length === 0) {
			return toast.success(labelSave);
		}

		const filter = savedFilters.find(obj => obj?.id === selectedFilter);
		const indexOfFilter = savedFilters.findIndex(obj => obj?.id === filter?.id);

		sAction.openPopup('SaveFilterPopup', 'LBL_SAVE_FILTER', true, { filters, module, id: selectedFilter, filterData: filter },
			(data) => {
				const newFilter = {
					id: Number(data.id),
					name: data.name,
					filters,
					roleIds: data.roleIds,
					isSystemFilter:
					data.isSystemFilter,
				};
				let newFilters = [...savedFilters];

				if (indexOfFilter !== -1) {
					newFilters[indexOfFilter] = newFilter;
				} else {
					newFilters = [...savedFilters, newFilter];
				}

				setSavedFilters(newFilters);
			});
	};

	/***/
	const onChangeSearch = (value) => {
		setSearch(value);
		setOffset(0);
	};

	/***/
	const onChangeSort = (sortValues) => {
		setDataSort(sortValues);
		setOffset(0);
		getList(sortValues);
	};

	/***/
	const selectAllRecords = () => {
		setChecked(!checked);
		let checkedRecords = listSelected || [];
		if (!checked) {
			viewData?.list?.data?.forEach((element) => {
				if (!disabledRecords?.includes(element.id) && !checkedRecords?.includes(element.id)) {
					checkedRecords.push(element.id);
				}
			});
		} else {
			viewData?.list?.data?.forEach((element) => {
				const index = checkedRecords.indexOf(element.id);
				if (index !== -1) {
					checkedRecords.splice(index, 1);
				}
			});
		}

		sAction.dsSet(`${prefix}/list/selected`, checkedRecords);
	};

	const globalTranslates = sAction.dsGet('config/translate/label');

	/***/
	const getList = (sortValues, preserveSelectedRows = false) => {
		if (!isPopup) {
			sAction?.contentLoad();
		} else {
			setPopupLoad(true);
		}

		if (!module) {
			return;
		}

		utils
			.post('getList', {
				module,
				search,
				offset,
				sort: sortValues,
				filters,
				additionalData: additionalData,
				fromModule,
				defaultFilter,
				onDashboard})
			.then((res) => {
				if (res.dataResult !== 'No Access' && res.data && res.data.header) {
					setError(false);
					if (res.data.dataSort?.length === 0 || dataSort === 0) {
						setDataSort({});
					} else {
						setDataSort(res.data.dataSort);
					}
					if (offset !== 0) {
						sAction.dsSet(`${prefix}/list/data`, res.data.data);
						setRows(res.data.data);
					}
					let setRecords = res.data.data;
					// if (disabledRecords?.length > 0) {
					// 	setRecords = res.data.data.filter(record => !disabledRecords?.some(id => id == record.id));
					// }
					let selectedRecordsInit = [];
					if (preserveSelectedRows) {
						selectedRecordsInit = listSelected;
					}
					setTimeout(() => {
						sAction.initList(
							res.data.header,
							setRecords,
							res.data.buttons,
							res.data.dataSort,
							res.data.timeline,
							prefix,
							listSelected
						);
						setSavedFilters(null);
						setSavedFilters(res.data.filters);
					}, 75);
				} else {
					if (res.dataResult === 'No Access') {
						setError('noAccess');
					} else {
						setError('notExists');
					}
				}
				if (!isPopup) {
					sAction.contentUnload();
				}
				setPopupLoad(false);
			})
			.catch((error) => {
				console.error(error);
			});
	};

	const errorMessagesTranslates = {
		noAccess: {
			message: utils.translate('LBL_NO_ACCESS'),
			info: utils.translate('LBL_NO_ACCESS_MESSAGE'),
		},
		notExists: {
			message: utils.translate('LBL_MODULE_NOT_EXISTS'),
			info: utils.translate('LBL_MODULE_NOT_EXISTS_INFO'),
		}
	};

	if (error) {
		return (
			<AppError
				errorMessage={error === 'noAccess' ? errorMessagesTranslates.noAccess.message : errorMessagesTranslates.notExists.message}
				infoMessage={error === 'noAccess' ? errorMessagesTranslates.noAccess.info : errorMessagesTranslates.notExists.info}
				icon="warning"
			/>
		);
	}

	return (
		<>
			{!onDashboard &&
		<div className="actionBox">
			{!isPopup ?
				<ActionButtons load={config?.contentLoad} userData={userData} prefix={prefix} viewData={viewData} buttons={viewData.list.buttons} /> :
				listSelected?.length > 0 ?
					<ActionButtons>
						<ButtonBasic
							icon={'checked'}
							onClick={() => popupSelectCallback(viewData?.list?.selected)}
							style={'green'}
							label={'LBL_CONFIRM'}>
							<span> ({listSelected?.length})</span>
						</ButtonBasic>
					</ActionButtons>
					: <div className="actionButtons"></div>
			}
			<FiltersSelect
				savedFilters={savedFilters}
				selectedFilter={selectedFilter}
				setSelectedFilter={setSelectedFilter}
				setFilters={setFilters}
				setSavedFilters={setSavedFilters}
			/>
			<SearchBox searchTimeout={true} module={module} onChangeSearch={(e) => onChangeSearch(e)} />
		</div>}
			<div className={`listView ${isPopup && !onDashboard ? 'listView__inPopup' : ''}`}>
				{popupLoad ? <div className="loadBoxListViewPopup"><Loader /></div> :
					<div id="listViewTable" className="listView__table">
						<table className="listView__table-box" cellPadding="0" cellSpacing="0">
							<thead>
								<ListViewHeader
									setSort={onChangeSort}
									globalTranslates={globalTranslates}
									sort={dataSort}
									headerColumns={viewData?.list?.header}
									selectAllRecords={selectAllRecords}
									checked={checked}
									setFilters={setFilters}
									filters={filters}
									prefix={prefix}
									saveFilters={saveFilters}
									module={module}
									disableMultiple={disableMultiple}
									selectedFilter={savedFilters.find(obj => obj.id === selectedFilter)}
									userId={userData?.usrId}
								/>
							</thead>
							<ListViewTable
								viewData={viewData}
								popupSelectCallback={popupSelectCallback}
								prefix={prefix}
								disableMultiple={disableMultiple}
								rows={rows}
								disabledRecords={disabledRecords}
								hiddenRecords={hiddenRecords}
							/>
						</table>
					</div>
				}
				<ListViewPagination
					numberOfRecords={numberOfRecords}
					rows={rows}
					setOffset={setOffset}
					offset={offset}
					onDashboard={onDashboard}
				/>
			</div>
		</>
	);
}
